/**
 * Box Truck → SMS. The team's text conversations with their drivers, over the company's own Quo lines.
 *
 * LEFT: one row per conversation, newest activity first, unread badged.
 * RIGHT: that conversation, oldest at the top, with a box to write the next one.
 * TOP-RIGHT: "New message" — pick any number of drivers from the roster and send them all the same
 * text (the install instructions, a policy change), which is the other half of why this page exists.
 * Past what one request can send, that message is QUEUED and leaves over the following minutes; the
 * strip under the header is the only place its progress is visible.
 *
 * A CONVERSATION IS `(line, counterpart)`, not the driver alone. The company writes to one driver from
 * several of its numbers — the HR line, a dispatcher's own — and to the driver each of those is a
 * different person in a different thread. So each row names the line it is on, and replying sends from
 * that line rather than from whichever number the operator happens to be assigned today.
 *
 * A thread is the TEAM's, not one recruiter's: whoever opens it sees everything said on it. Replies
 * arrive over the socket, so an open thread appends without a refetch and the list re-sorts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MessageSquare, Plus } from 'lucide-react'
import { SmsConversationSummary, SmsThreadMessage } from '@numeo/types'
import { TeamGroupSummary } from '@numeo/types-main-web-app'
import { Button } from '@/components/ds2'
import { useSnackbar } from '@/contexts/SnackbarContext'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { SnackbarType } from '@/utils/snackbar'
import { fetchAllSmsConversations, fetchSmsThread, markSmsThreadRead } from '../services/boxTruckLoadsApi'
import { BroadcastProgressStrip } from '../components/BroadcastProgressStrip'
import { SmsBulkModal } from '../components/SmsBulkModal'
import { SmsConversationList, UNGROUPED } from '../components/SmsConversationList'
import { useDriverGroupBadges } from '../hooks/useDriverGroupBadges'
import { SmsThread } from '../components/SmsThread'
import { trackBoxTruckUiInteraction } from '../utils/analytics'
import { deskFilterKind, filterConversations, groupByDay, keyOf } from '../utils/smsThread'
import { useSmsComposer } from '../hooks/useSmsComposer'

/** The socket event the WS middleware re-dispatches as a window CustomEvent. */
interface SmsReceivedDetail {
    counterpart: string
    line: string
    linePhoneNumberId: string | null
    driverAccountId: string | null
    /** The fleet driver record id — what the desk badge and its filter key on. */
    driverId: string | null
    name: string | null
    message: SmsThreadMessage
}

export const BoxTruckSmsPage = () => {
    const { showSnackbar } = useSnackbar()
    const [conversations, setConversations] = useState<SmsConversationSummary[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    /** The desk being shown: a group id, `UNGROUPED`, or null for all. */
    const [groupFilter, setGroupFilter] = useState<string | null>(null)
    const [selected, setSelected] = useState<string | null>(null)
    const [messages, setMessages] = useState<SmsThreadMessage[]>([])
    const [threadLoading, setThreadLoading] = useState(false)

    const [bulkOpen, setBulkOpen] = useState(false)
    /** Bumped when a send was too big to go out at once, so the strip starts watching it immediately
     *  instead of on its next poll. */
    const [broadcastKey, setBroadcastKey] = useState(0)
    const bottomRef = useRef<HTMLDivElement | null>(null)
    // The open thread, read inside the socket handler without making it a dependency — re-subscribing on
    // every selection would drop events in the gap.
    const selectedRef = useRef<string | null>(null)
    selectedRef.current = selected

    /**
     * Load every conversation, a page at a time, painting each page as it lands.
     *
     * The whole list is kept because the search box filters what is loaded — a half-loaded list is a
     * search that quietly misses people. `setLoading(false)` fires on the FIRST page so the operator is
     * reading while the rest arrives, which is the point of paging at all.
     */
    const loadConversations = useCallback(
        async (afterSend = false) => {
            try {
                await fetchAllSmsConversations((soFar) => {
                    setConversations((prev) => {
                        // MERGED, not replaced. The walk takes a few seconds and the socket is live
                        // throughout: a message arriving mid-walk is already in state, and a plain replace
                        // would drop it off the list until the next load — a text that arrived and then
                        // vanished. Whichever copy is newer wins, so the server's own rows still correct
                        // anything stale.
                        const byKey = new Map(soFar.map((row) => [keyOf(row.line, row.counterpart), row]))
                        for (const row of prev) {
                            const key = keyOf(row.line, row.counterpart)
                            const fromServer = byKey.get(key)
                            if (!fromServer || row.lastAt > fromServer.lastAt) byKey.set(key, row)
                        }
                        return [...byKey.values()].sort((a, b) => b.lastAt.localeCompare(a.lastAt))
                    })
                    setLoading(false)
                }, afterSend)
            } catch (e) {
                showSnackbar(SnackbarType.ERROR, getUserErrorMessage(e) || 'Could not load the conversations')
            } finally {
                setLoading(false)
            }
        },
        [showSnackbar]
    )

    useEffect(() => {
        void loadConversations()
    }, [loadConversations])

    const openThread = useCallback(
        async (conversation: SmsConversationSummary) => {
            const key = keyOf(conversation.line, conversation.counterpart)
            setSelected(key)
            selectedRef.current = key
            setThreadLoading(true)
            try {
                const messages = await fetchSmsThread(conversation.counterpart, conversation.line)
                // Click A, click B, A answers last: without this the header says B and the messages
                // are A's — and a reply typed into that goes to B, from a screen showing A's history.
                if (selectedRef.current !== key) return
                setMessages(messages)
                // Clearing the badge is not worth failing the open over — but a silent swallow left the
                // row unread with nothing anywhere to say why, so it is logged.
                void markSmsThreadRead(conversation.counterpart, conversation.line).catch((e) =>
                    console.error('[box-truck] could not clear the SMS unread badge', e)
                )
                setConversations((prev) => prev.map((c) => (keyOf(c.line, c.counterpart) === key ? { ...c, unread: 0 } : c)))
            } catch (e) {
                if (selectedRef.current !== key) return
                showSnackbar(SnackbarType.ERROR, getUserErrorMessage(e) || 'Could not load the conversation')
            } finally {
                if (selectedRef.current === key) setThreadLoading(false)
            }
        },
        [showSnackbar]
    )

    // Live arrivals: append to the open thread, and move the row up the list with a badge otherwise.
    //
    // Two kinds come through here. A driver's reply, and a message the tenant wrote in the Quo app —
    // which is OURS, so it badges nothing and leaves the thread read. Reading the direction off the
    // message rather than assuming it is what keeps the list honest about who spoke last.
    useEffect(() => {
        const onReceived = (event: Event) => {
            const detail = (event as CustomEvent<SmsReceivedDetail>).detail
            if (!detail?.counterpart || !detail.line) return
            const key = keyOf(detail.line, detail.counterpart)
            const isOpen = selectedRef.current === key
            const inbound = detail.message.direction === 'in'
            if (isOpen) {
                setMessages((prev) => (prev.some((m) => m.id === detail.message.id) ? prev : [...prev, detail.message]))
                if (inbound) {
                    void markSmsThreadRead(detail.counterpart, detail.line).catch((e) =>
                        console.error('[box-truck] could not clear the SMS unread badge on a live reply', e)
                    )
                }
            }
            setConversations((prev) => {
                const existing = prev.find((c) => keyOf(c.line, c.counterpart) === key)
                const row: SmsConversationSummary = existing
                    ? {
                          ...existing,
                          lastMessage: detail.message.body,
                          lastDirection: detail.message.direction,
                          lastSource: detail.message.source,
                          lastAt: detail.message.createdAt,
                          // Open means it is being read, and the read is already on its way. Our own
                          // message from the Quo app badges nothing and clears nothing.
                          unread: isOpen ? 0 : inbound ? existing.unread + 1 : existing.unread,
                          // A row the list fetched without one keeps whatever the arrival knows, so a
                          // thread whose desk the server has only just resolved stops reading as "No group".
                          driverId: existing.driverId ?? detail.driverId ?? null,
                      }
                    : {
                          counterpart: detail.counterpart,
                          line: detail.line,
                          linePhoneNumberId: detail.linePhoneNumberId,
                          driverAccountId: detail.driverAccountId,
                          driverId: detail.driverId ?? null,
                          name: detail.name,
                          lastMessage: detail.message.body,
                          lastDirection: detail.message.direction,
                          lastSource: detail.message.source,
                          lastAt: detail.message.createdAt,
                          unread: isOpen || !inbound ? 0 : 1,
                      }
                return [row, ...prev.filter((c) => keyOf(c.line, c.counterpart) !== key)]
            })
        }
        window.addEventListener('box-truck:sms-received', onReceived)
        return () => window.removeEventListener('box-truck:sms-received', onReceived)
    }, [])

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ block: 'end' })
    }, [messages])

    const current = useMemo(() => conversations.find((c) => keyOf(c.line, c.counterpart) === selected) ?? null, [conversations, selected])

    /**
     * The desk each conversation's driver is on, in ONE request for the whole inbox.
     *
     * `'all'` rather than the list's ids: this page holds every conversation at once (that is what the
     * paging walk is for), so the ids would not fit a query string and chunking them would make the server
     * repeat its group reads per chunk for an answer it computes in memory. One request, and its cost is
     * the tenant's group count rather than its driver count.
     *
     * Decoration, like everywhere else it is drawn: a failure leaves the rows without badges and the filter
     * unoffered, rather than holding up the inbox.
     */
    const groupBadges = useDriverGroupBadges('all')
    const groupOf = useCallback(
        (conversation: SmsConversationSummary) => (conversation.driverId ? (groupBadges.get(conversation.driverId) ?? null) : null),
        [groupBadges]
    )

    /**
     * The buckets to offer, from the UNFILTERED list: the desks present, by name, and whether anything is
     * on no desk at all.
     *
     * By NAME rather than by first appearance, which is what the list's own order would give: a message
     * arriving re-sorts the list, and chips that reshuffle under the cursor are worse than alphabetical
     * ones. Unfiltered, because deriving them from what is on screen would take the chip away the moment
     * it is used — select "No group" and the last ungrouped row is the only one left to derive it from.
     *
     * Derived from the badges rather than from the group list, because a dispatcher cannot read the group
     * list at all (`GET /team-groups/v1` is owner/admin-only) and these are exactly the desks they may see.
     */
    const { groupOptions, hasUngrouped } = useMemo(() => {
        const seen = new Map<string, TeamGroupSummary>()
        let ungrouped = false
        for (const conversation of conversations) {
            const group = conversation.driverId ? groupBadges.get(conversation.driverId) : undefined
            if (group) seen.set(group.id, group)
            else ungrouped = true
        }
        return { groupOptions: [...seen.values()].sort((a, b) => a.name.localeCompare(b.name)), hasUngrouped: ungrouped }
    }, [conversations, groupBadges])

    /** Which KIND of bucket was chosen, never which desk — the name is the tenant's own vocabulary. */
    const onGroupFilter = useCallback((groupId: string | null) => {
        setGroupFilter(groupId)
        trackBoxTruckUiInteraction('sms_desk_filter', deskFilterKind(groupId, UNGROUPED))
    }, [])

    /** The search and the desk, both over the WHOLE loaded list and neither of them a request — see
     *  `filterConversations`, which is where the rule lives. */
    const filtered = useMemo(
        () => filterConversations(conversations, { search, groupFilter, ungrouped: UNGROUPED, groupIdOf: (c) => groupOf(c)?.id ?? null }),
        [conversations, search, groupFilter, groupOf]
    )

    /** How many of the company's numbers are in play — the line is only worth naming when there are two. */
    const multiLine = useMemo(() => new Set(conversations.map((c) => c.line)).size > 1, [conversations])

    /** Messages with a day heading inserted wherever the date changes. */
    const grouped = useMemo(() => groupByDay(messages), [messages])

    /**
     * Re-read the thread rather than guess at it: the row the server wrote carries its id, status and
     * line, and `unconfirmed` is a status only it knows.
     *
     * Its own try/catch — the message is already handed over, so a failed refresh must not be reported
     * as a failed send — and it writes nothing if the operator has moved to another conversation.
     */
    const refreshThread = async (conversation: SmsConversationSummary, stillOpen: () => boolean) => {
        try {
            const thread = await fetchSmsThread(conversation.counterpart, conversation.line)
            if (stillOpen()) setMessages(thread)
        } catch {
            if (stillOpen()) showSnackbar(SnackbarType.WARNING, 'Sent — but the conversation could not be refreshed')
        }
    }

    /**
     * The composer, shared with the load drawer's panel: same four outcomes, same analytics, same
     * handling of a send that may have gone out. What stays here is this page's own bookkeeping —
     * which conversation is open, and the list row that has to show what was just said.
     */
    const { draft, setDraft, sending, send } = useSmsComposer({
        surface: 'inbox',
        thread: current ? { driverAccountId: current.driverAccountId, counterpart: current.counterpart, line: current.line } : null,
        threadKey: selected,
        stillOpen: () => !!current && selectedRef.current === keyOf(current.line, current.counterpart),
        refresh: async () => {
            if (!current) return
            const key = keyOf(current.line, current.counterpart)
            await refreshThread(current, () => selectedRef.current === key)
        },
        onSent: (text) => {
            if (!current) return
            const key = keyOf(current.line, current.counterpart)
            setConversations((prev) =>
                prev.map((c) =>
                    keyOf(c.line, c.counterpart) === key ? { ...c, lastMessage: text, lastDirection: 'out', lastAt: new Date().toISOString() } : c
                )
            )
        },
    })

    return (
        <div className="flex h-full flex-col bg-background text-foreground">
            <div className="flex items-center justify-between gap-3 border-b border-border px-6 py-3">
                <div>
                    <h1 className="flex items-center gap-2 text-base font-semibold">
                        <MessageSquare size={18} className="text-accent" /> SMS
                    </h1>
                    <p className="text-xs text-foreground-muted">Text your drivers from the company's Quo line, and read what they reply</p>
                </div>
                {/* The load bot's switches are NOT here. Both of them — this conversation's and the
                    company's — sit on one bar above the composer, where the conversation they are about is
                    being read (ADR-057 §14). A company switch up here said "Load bot on" over a driver the
                    bot had been paused for, which reads as the bot being broken. */}
                <Button variant="primary" size="sm" prefix={<Plus size={14} />} onClick={() => setBulkOpen(true)}>
                    New message
                </Button>
            </div>

            <BroadcastProgressStrip refreshKey={broadcastKey} />

            <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[340px_1fr]">
                <SmsConversationList
                    conversations={filtered}
                    loading={loading}
                    search={search}
                    selected={selected}
                    multiLine={multiLine}
                    groupOf={groupOf}
                    groupOptions={groupOptions}
                    hasUngrouped={hasUngrouped}
                    groupFilter={groupFilter}
                    onGroupFilter={onGroupFilter}
                    onSearch={setSearch}
                    onOpen={(conversation) => void openThread(conversation)}
                />

                <SmsThread
                    conversation={current}
                    messages={grouped}
                    loading={threadLoading}
                    draft={draft}
                    sending={sending}
                    bottomRef={bottomRef}
                    onDraft={setDraft}
                    onSend={() => void send()}
                />
            </div>

            {bulkOpen && (
                <SmsBulkModal onClose={() => setBulkOpen(false)} onSent={() => void loadConversations(true)} onQueued={() => setBroadcastKey((k) => k + 1)} />
            )}
        </div>
    )
}
